import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { ViewPaths, ViewConfig } from '@/types/views';
import {
  getDefaultView,
  getViewsForType,
  hasViewPermission,
  isValidView,
} from '@/utils/viewHelpers';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DataNotFound } from '@/components/DataNotFound';
import Header from '@/components/Header/Header';
import { useIntl, FormattedMessage } from 'react-intl';
import { HeaderMenu } from '@/components/Header/HeaderMenu';
import { DepartmentAdmins } from '@/components/Departments/DepartmentAdmins';
import { DepartmentPayerForm } from '@/components/Departments/DepartmentPayerForm';
import { CenteredColumn } from '@/components/CenteredColumn';
import { Section } from '@/components/Section';
import { ActionButtons } from '@/components/Header/ActionButtons';
import Button from '@/components/core/Button';
import { IconUserPlus, IconDataImport } from '@/icons';
import { StudentList } from '@/components/Students/StudentList';
import { CollectionImportList } from '@/components/Import/CollectionImportList';
import { DialogWindow } from '@/components/Dialog';
import { StudentProfileForm } from '@/components/Students/StudentProfileForm';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useGetStudentCollection } from '@/hooks/useGetStudentCollection';
import { StudentCategoryPeriods } from '@/components/Organizations/StudentCategoryPeriods';
import { Loading } from '@/components/Loading';
import { useCollectionClaimWarning } from '@/hooks/useCollectionClaimWarning';
import { useStudentCategoryEmpty } from '@/hooks/useStudentCategoryEmpty';
import { StudentCategoryInfo } from '@/components/Students/StudentCategoryInfo';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';

export const Route = createFileRoute(
  '/$tenantId/_layout/students/transportation/$view',
)({
  component: StudentTransportationView,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      status: search.status as string[],
      halfProduct: search.halfProduct as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
      manuallyModified: search.manuallyModified as string[],
      claimState: search.claimState as string[],
    };
  },
  beforeLoad: ({ params: { tenantId, view } }) => {
    if (
      !isValidView(view, 'students/transportation') ||
      !hasViewPermission(view, 'students/transportation', 'organization:viewer')
    ) {
      throw redirect({
        to: '/$tenantId/students/transportation/$view',
        params: {
          tenantId,
          view: getDefaultView('students/transportation'),
        },
      });
    }
  },
});

function StudentTransportationView() {
  const { tenantId, view } = Route.useParams();
  const selected = view as ViewPaths<'students/transportation'>;
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { students, collectionId, collection, department, isLoading } =
    useGetStudentCollection('transportation');
  const { hasWarning: hasClaimWarning } =
    useCollectionClaimWarning(collectionId);
  const isCategoryEmpty = useStudentCategoryEmpty(collectionId, students);

  const [open, setOpen] = useState<boolean>(false);

  const onViewChange = useCallback(
    (view?: string) => {
      if (!view) return;

      navigate({
        to: Route.to,
        params: { tenantId, view },
        replace: false,
      });
    },
    [navigate, tenantId],
  );

  const onChange = () => {
    setOpen(true);
  };

  const views = useMemo(() => {
    return getViewsForType('students/transportation') as ViewConfig[];
  }, []);

  const metadataKeys = useMemo(() => {
    switch (selected) {
      case 'list':
        return {
          titleKey: 'page.students.transportation.list.title',
          descriptionKey: 'page.students.transportation.list.description',
        };
      case 'admins':
        return {
          titleKey: 'page.administrators.title',
          descriptionKey: 'page.administrators.description',
        };
      case 'invoicing':
        return {
          titleKey: 'page.students.invoicing.title',
          descriptionKey: 'page.students.invoicing.description',
        };
      case 'periods':
        return {
          titleKey: 'page.students.transportation.periods.title',
          descriptionKey: 'page.students.transportation.periods.description',
        };
      case 'imports':
        return {
          titleKey: 'page.imports.title',
          descriptionKey: 'page.imports.description',
        };
      case 'info':
        return {
          titleKey: 'page.students.transportation.info.title',
          descriptionKey: 'page.students.transportation.info.description',
        };
      default:
        return {
          titleKey: 'page.students.transportation.title',
          descriptionKey: 'page.students.transportation.description',
        };
    }
  }, [selected]);

  if (isLoading) {
    return <Loading />;
  }

  if (!students) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Kan inte hämta lista på elever" />
        }
        buttonTitle={<FormattedMessage defaultMessage="Till start" />}
        onClick={() => navigate({ to: '/$tenantId', params: { tenantId } })}
      />
    );
  }

  return (
    <ViewPermissions permission="organization:viewer">
      <div className="flex grow flex-col overflow-y-auto">
        <DocumentHead
          titleKey={metadataKeys.titleKey}
          descriptionKey={metadataKeys.descriptionKey}
        />
        <Header
          title={formatMessage({ defaultMessage: 'Skolskjuts' })}
          menu={
            <HeaderMenu
              options={views}
              selected={selected}
              onViewChange={onViewChange}
              notificationViews={[
                ...(hasClaimWarning ? ['periods'] : []),
                ...(isCategoryEmpty ? ['info'] : []),
              ]}
              notificationLabels={{
                periods: formatMessage({
                  defaultMessage: 'Ingen aktiv period',
                }),
                info: formatMessage({
                  defaultMessage: 'Kategorin saknar personer',
                }),
              }}
            />
          }
          actions={
            <ViewPermissions
              permission="organization:editor"
              departmentId={department?.id}
            >
              {department && hasDepartmentWriteAccess(department.id) && (
                <ActionButtons>
                  <Button
                    startIcon={<IconUserPlus />}
                    onClick={onChange}
                    variant="outline"
                  >
                    <FormattedMessage defaultMessage="Ny elev" />
                  </Button>
                  <Button
                    startIcon={<IconDataImport />}
                    onClick={() => {
                      navigate({
                        to: '/$tenantId/students/transportation/import',
                        params: { tenantId },
                      });
                    }}
                    variant="outline"
                  >
                    <FormattedMessage defaultMessage="Importera" />
                  </Button>
                </ActionButtons>
              )}
            </ViewPermissions>
          }
        />
        {selected === 'admins' && department && (
          <div className="w-full px-4 md:px-8">
            <DepartmentAdmins departmentId={department.id} />
          </div>
        )}
        {selected === 'invoicing' && department && (
          <CenteredColumn>
            <Section>
              <DepartmentPayerForm departmentId={department.id} />
            </Section>
          </CenteredColumn>
        )}
        {selected === 'list' && (
          <StudentList
            students={students}
            routeType={Route}
            studentType="transportation"
            collectionId={collectionId ?? undefined}
          />
        )}
        {selected === 'periods' && collectionId && (
          <CenteredColumn>
            <Section>
              <StudentCategoryPeriods
                collectionId={collectionId}
                collectionType={collection?.type as CollectionTypeDTO}
              />
            </Section>
          </CenteredColumn>
        )}
        {selected === 'imports' && collectionId && (
          <CollectionImportList
            collectionId={collectionId}
            studentType="transportation"
            tenantId={tenantId}
          />
        )}
        {selected === 'info' && (
          <CenteredColumn className="grow">
            <StudentCategoryInfo studentType="transportation" />
          </CenteredColumn>
        )}
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Lägg till ny elev' })}
          content={
            <StudentProfileForm
              onOpenChange={setOpen}
              type="transportation"
              collectionId={collectionId ?? undefined}
              departmentId={department?.id}
            />
          }
          onOpenChange={setOpen}
          open={open}
        />
      </div>
    </ViewPermissions>
  );
}
